import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL as CONFIG_BACKEND_URL } from '../config/api';

const BACKEND_URL = CONFIG_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Data state
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [activeTab, setActiveTab] = useState('orders');
  
  // Upload state
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Email state
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  // Message state
  const [adminMessage, setAdminMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // Latest Files state
  const [latestFiles, setLatestFiles] = useState([]);
  const [latestFilesLoading, setLatestFilesLoading] = useState(false);
  const [showLatestFileForm, setShowLatestFileForm] = useState(false);
  const [editingLatestFile, setEditingLatestFile] = useState(null);
  const [latestFileForm, setLatestFileForm] = useState({
    calibration_no: '',
    ecu_type: '',
    vehicle_details: '',
    brand: '',
    year_range: '',
    engine_type: '',
    description: '',
    thumbnail_url: '',
    original_file_url: '',
    original_file_name: '',
    original_price: '',
    modification_type: '',
    modified_file_url: '',
    modified_file_name: '',
    modified_price: '',
    has_dcu: false,
    dcu_calibration_no: '',
    dcu_ecu_type: '',
    original_dcu_url: '',
    original_dcu_name: '',
    modified_dcu_url: '',
    modified_dcu_name: '',
    is_featured: false
  });
  const [uploadingLatestFile, setUploadingLatestFile] = useState(false);
  const [uploadingFileType, setUploadingFileType] = useState('');

  // Check for existing session
  useEffect(() => {
    const checkSession = async () => {
      const adminToken = sessionStorage.getItem('adminToken');
      if (adminToken) {
        try {
          const response = await axios.post(`${API}/admin/verify-session`, null, {
            params: { token: adminToken }
          });
          if (response.data.valid) {
            setIsAuthenticated(true);
          } else {
            sessionStorage.removeItem('adminToken');
          }
        } catch (error) {
          sessionStorage.removeItem('adminToken');
        }
      }
    };
    checkSession();
  }, []);

  // Admin Login Handler - Now uses secure backend API
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, {
        username: loginUsername,
        password: loginPassword
      });
      
      if (response.data.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem('adminToken', response.data.token);
      }
    } catch (error) {
      setLoginError(error.response?.data?.detail || 'Invalid username or password');
    } finally {
      setLoginLoading(false);
    }
  };

  // Admin Logout Handler
  const handleAdminLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminToken');
  };

  // Notification sound
  const playNotification = () => {
    const audio = new Audio('data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU...');
    audio.play().catch(() => {});
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
      // Poll for new orders every 30 seconds
      const interval = setInterval(fetchOrders, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const fetchOrders = async () => {
    try {
      // Fetch combined orders from admin endpoint (includes both collections)
      const response = await axios.get(`${API}/admin/orders`);
      const allOrders = response.data || [];
      
      // Check for new orders (for notification sound)
      if (orders.length > 0 && allOrders.length > orders.length) {
        playNotification();
      }
      
      setOrders(allOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, newStatus, notifyCustomer = true) => {
    try {
      const response = await axios.patch(`${API}/admin/order/${orderId}/status`, { 
        status: newStatus,
        notify_customer: notifyCustomer 
      });
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
      
      // Show notification status
      const notifications = [];
      if (response.data.email_sent) notifications.push('📧 Email');
      if (response.data.whatsapp_sent) notifications.push('📱 WhatsApp');
      
      if (notifications.length > 0) {
        alert(`✅ Status updated to ${newStatus}\n\nNotifications sent: ${notifications.join(', ')}`);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating status');
    }
  };

  const uploadModifiedFile = async (file) => {
    if (!file || !selectedOrder) return;
    
    setUploadingFile(true);
    setUploadProgress(0);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('order_id', selectedOrder.id);
    formData.append('send_notification', 'true'); // Auto-notify customer
    
    try {
      const response = await axios.post(`${API}/admin/upload-modified`, formData, {
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      if (response.data.email_sent || response.data.whatsapp_sent) {
        const notifications = [];
        if (response.data.email_sent) notifications.push('📧 Email');
        if (response.data.whatsapp_sent) notifications.push('📱 WhatsApp');
        alert(`✅ Modified file uploaded successfully!\n\nNotifications sent: ${notifications.join(', ')}`);
      } else {
        alert('✅ Modified file uploaded successfully!');
      }
      fetchOrders();
      
      // Refresh selected order
      const updated = orders.find(o => o.id === selectedOrder.id);
      if (updated) setSelectedOrder(updated);
      
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file');
    } finally {
      setUploadingFile(false);
      setUploadProgress(0);
    }
  };

  const sendDownloadEmail = async () => {
    if (!selectedOrder) return;
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/admin/send-download-email`, {
        order_id: selectedOrder.id,
        email: selectedOrder.customer_email
      });
      
      setEmailSent(true);
      setTimeout(() => setEmailSent(false), 3000);
      alert('Download link sent to customer!');
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingEmail(false);
    }
  };

  const sendAdminMessage = async () => {
    if (!adminMessage.trim() || !selectedOrder) return;
    
    setSendingMessage(true);
    try {
      const formData = new FormData();
      formData.append('order_id', selectedOrder.id);
      formData.append('message', adminMessage.trim());
      formData.append('send_email', 'true');
      
      const response = await axios.post(`${API}/admin/portal/message`, formData);
      
      setAdminMessage('');
      
      const notifications = [];
      if (response.data.email_sent) notifications.push('📧 Email');
      if (response.data.whatsapp_sent) notifications.push('📱 WhatsApp');
      
      if (notifications.length > 0) {
        alert(`✅ Message sent to customer!\n\nNotifications: ${notifications.join(', ')}`);
      } else {
        alert('✅ Message sent to customer portal.');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  // Delete order function
  const deleteOrder = async (orderId) => {
    try {
      await axios.delete(`${API}/admin/order/${orderId}`);
      alert('✅ Order deleted successfully');
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(null);
      }
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('Failed to delete order: ' + (error.response?.data?.detail || error.message));
    }
  };

  const downloadFile = async (filename) => {
    try {
      window.open(`${API}/download/${filename}`, '_blank');
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error downloading file');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700',
      pending_payment: 'bg-yellow-100 text-yellow-700',
      paid: 'bg-green-100 text-green-700',
      processing: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
      refunded: 'bg-orange-100 text-orange-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Stats
  const totalOrders = orders.length;
  const pendingOrders = orders.filter(r => r.status === 'pending' || r.status === 'pending_payment').length;
  const processingOrders = orders.filter(r => r.status === 'processing' || r.status === 'paid').length;
  const completedOrders = orders.filter(r => r.status === 'completed').length;
  const totalRevenue = orders.filter(r => r.payment_status === 'paid' || r.status === 'completed').reduce((sum, o) => sum + (o.total_amount || o.price || 0), 0);

  // Latest Files Functions
  const fetchLatestFiles = async () => {
    setLatestFilesLoading(true);
    try {
      const response = await axios.get(`${API}/admin/latest-files`);
      setLatestFiles(response.data);
    } catch (error) {
      console.error('Error fetching latest files:', error);
    } finally {
      setLatestFilesLoading(false);
    }
  };

  const handleLatestFileUpload = async (e, fileType) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingLatestFile(true);
    setUploadingFileType(fileType);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'latest-files');
    
    try {
      const response = await axios.post(`${API}/admin/latest-files/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        // Update the appropriate field based on fileType
        const urlField = `${fileType}_url`;
        const nameField = `${fileType}_name`;
        setLatestFileForm(prev => ({
          ...prev,
          [urlField]: response.data.file_url,
          [nameField]: response.data.file_name
        }));
        alert(`File uploaded successfully!`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert(`Upload failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploadingLatestFile(false);
      setUploadingFileType('');
    }
  };

  const handleLatestFileFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setLatestFileForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetLatestFileForm = () => {
    setLatestFileForm({
      calibration_no: '',
      ecu_type: '',
      vehicle_details: '',
      brand: '',
      year_range: '',
      engine_type: '',
      description: '',
      thumbnail_url: '',
      original_file_url: '',
      original_file_name: '',
      original_price: '',
      modification_type: '',
      modified_file_url: '',
      modified_file_name: '',
      modified_price: '',
      has_dcu: false,
      dcu_calibration_no: '',
      dcu_ecu_type: '',
      original_dcu_url: '',
      original_dcu_name: '',
      modified_dcu_url: '',
      modified_dcu_name: '',
      is_featured: false
    });
    setEditingLatestFile(null);
    setShowLatestFileForm(false);
  };

  const handleSaveLatestFile = async () => {
    // Validate required fields
    if (!latestFileForm.calibration_no || !latestFileForm.ecu_type || !latestFileForm.vehicle_details) {
      alert('Please fill in all required fields');
      return;
    }
    if (!latestFileForm.original_file_url || !latestFileForm.modified_file_url) {
      alert('Please upload both Original and Modified files');
      return;
    }
    if (!latestFileForm.original_price || !latestFileForm.modified_price) {
      alert('Please set prices for both Original and Modified files');
      return;
    }
    
    try {
      const payload = {
        ...latestFileForm,
        original_price: parseFloat(latestFileForm.original_price),
        modified_price: parseFloat(latestFileForm.modified_price)
      };
      
      if (editingLatestFile) {
        await axios.put(`${API}/admin/latest-files/${editingLatestFile.id}`, payload);
        alert('File updated successfully!');
      } else {
        await axios.post(`${API}/admin/latest-files`, payload);
        alert('File created successfully!');
      }
      
      resetLatestFileForm();
      fetchLatestFiles();
    } catch (error) {
      console.error('Save error:', error);
      alert(`Save failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleEditLatestFile = (file) => {
    setLatestFileForm({
      calibration_no: file.calibration_no || '',
      ecu_type: file.ecu_type || '',
      vehicle_details: file.vehicle_details || '',
      brand: file.brand || '',
      year_range: file.year_range || '',
      engine_type: file.engine_type || '',
      description: file.description || '',
      thumbnail_url: file.thumbnail_url || '',
      original_file_url: file.original_file_url || '',
      original_file_name: file.original_file_name || '',
      original_price: file.original_price?.toString() || '',
      modification_type: file.modification_type || '',
      modified_file_url: file.modified_file_url || '',
      modified_file_name: file.modified_file_name || '',
      modified_price: file.modified_price?.toString() || '',
      has_dcu: file.has_dcu || false,
      dcu_calibration_no: file.dcu_calibration_no || '',
      dcu_ecu_type: file.dcu_ecu_type || '',
      original_dcu_url: file.original_dcu_url || '',
      original_dcu_name: file.original_dcu_name || '',
      modified_dcu_url: file.modified_dcu_url || '',
      modified_dcu_name: file.modified_dcu_name || '',
      is_featured: file.is_featured || false
    });
    setEditingLatestFile(file);
    setShowLatestFileForm(true);
  };

  const handleDeleteLatestFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    
    try {
      await axios.delete(`${API}/admin/latest-files/${fileId}`);
      alert('File deleted successfully!');
      fetchLatestFiles();
    } catch (error) {
      console.error('Delete error:', error);
      alert(`Delete failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleToggleLatestFileActive = async (file) => {
    try {
      await axios.put(`${API}/admin/latest-files/${file.id}`, {
        is_active: !file.is_active
      });
      fetchLatestFiles();
    } catch (error) {
      console.error('Toggle error:', error);
      alert(`Failed to update: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Admin Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl mb-4">
              <span className="text-3xl">⚡</span>
            </div>
            <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
            <p className="text-gray-400">ECU Flash Service</p>
          </div>
          
          <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
            <form onSubmit={handleAdminLogin}>
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Username</label>
                  <input
                    type="text"
                    value={loginUsername}
                    onChange={(e) => setLoginUsername(e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder="Enter username"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                  <input
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder="Enter password"
                    required
                  />
                </div>
                
                {loginError && (
                  <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-3 text-red-400 text-sm">
                    {loginError}
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={loginLoading}
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-400 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition disabled:opacity-50"
                >
                  {loginLoading ? 'Signing In...' : 'Sign In to Admin'}
                </button>
              </div>
            </form>
            
            <div className="mt-6 pt-6 border-t border-gray-700 text-center">
              <button
                onClick={() => navigate('/')}
                className="text-gray-400 hover:text-white text-sm"
              >
                ← Back to Website
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-gray-900 text-white flex flex-col">
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
              <span className="text-xl">⚡</span>
            </div>
            <div>
              <h1 className="font-bold">ECU Flash</h1>
              <p className="text-xs text-gray-400">Admin Panel</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-2">
          <button
            onClick={() => setActiveTab('orders')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition ${
              activeTab === 'orders' ? 'bg-blue-600' : 'hover:bg-gray-800'
            }`}
          >
            <span>📦</span> Orders
            {pendingOrders > 0 && (
              <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{pendingOrders}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition ${
              activeTab === 'stats' ? 'bg-blue-600' : 'hover:bg-gray-800'
            }`}
          >
            <span>📊</span> Statistics
          </button>
          <button
            onClick={() => { setActiveTab('latest-files'); fetchLatestFiles(); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition ${
              activeTab === 'latest-files' ? 'bg-blue-600' : 'hover:bg-gray-800'
            }`}
          >
            <span>📁</span> Latest Files
          </button>
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left hover:bg-gray-800 transition"
          >
            <span>🏠</span> Back to Site
          </button>
          <button
            onClick={handleAdminLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left text-red-400 hover:bg-red-500/20 transition mt-4"
          >
            <span>🚪</span> Logout
          </button>
        </nav>
        
        {/* Quick Stats */}
        <div className="p-4 border-t border-gray-800">
          <div className="text-gray-400 text-xs mb-2">TODAY'S REVENUE</div>
          <div className="text-2xl font-bold text-green-400">${totalRevenue.toFixed(2)}</div>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-xl text-gray-500">Loading...</div>
          </div>
        ) : (
          <>
            {activeTab === 'orders' && (
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Orders</h2>
                  <button
                    onClick={fetchOrders}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-xl transition"
                  >
                    🔄 Refresh
                  </button>
                </div>
                
                {/* Stats Cards */}
                <div className="grid grid-cols-5 gap-4 mb-6">
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <div className="text-2xl font-bold text-gray-900">{totalOrders}</div>
                    <div className="text-gray-500 text-sm">Total Orders</div>
                  </div>
                  <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                    <div className="text-2xl font-bold text-yellow-600">{pendingOrders}</div>
                    <div className="text-yellow-600 text-sm">Pending</div>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                    <div className="text-2xl font-bold text-blue-600">{processingOrders}</div>
                    <div className="text-blue-600 text-sm">Processing</div>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <div className="text-2xl font-bold text-green-600">{completedOrders}</div>
                    <div className="text-green-600 text-sm">Completed</div>
                  </div>
                  <div className="bg-gray-900 rounded-xl p-4">
                    <div className="text-2xl font-bold text-green-400">${totalRevenue.toFixed(2)}</div>
                    <div className="text-gray-400 text-sm">Revenue</div>
                  </div>
                </div>
                
                {/* Orders Table */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Date</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Customer</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Vehicle</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Services</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Amount</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Status</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                            No orders yet. Orders will appear here when customers place them.
                          </td>
                        </tr>
                      ) : (
                        orders.map((order) => (
                          <tr
                            key={order.id}
                            className={`border-t border-gray-100 hover:bg-gray-50 cursor-pointer ${
                              selectedOrder?.id === order.id ? 'bg-blue-50' : ''
                            }`}
                            onClick={() => setSelectedOrder(order)}
                          >
                            <td className="px-4 py-3 text-sm text-gray-700">{formatDate(order.created_at)}</td>
                            <td className="px-4 py-3">
                              <div className="text-sm font-medium text-gray-900">{order.customer_name}</div>
                              <div className="text-xs text-gray-500">{order.customer_email}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {order.vehicle_info || 
                               (order.vehicle_make && `${order.vehicle_year || ''} ${order.vehicle_make} ${order.vehicle_model || ''}`.trim()) ||
                               '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {(order.services || order.selected_services || order.purchased_services || []).slice(0, 2).map(s => typeof s === 'object' ? (s.name || s.service_name) : s).join(', ') || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm font-semibold text-green-600">${(order.total_amount || order.total_price || order.price || 0).toFixed(2)}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                                {order.status || 'pending'}
                              </span>
                            </td>
                            <td className="px-4 py-3 flex items-center gap-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); setSelectedOrder(order); }}
                                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                              >
                                View
                              </button>
                              <button
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  if (window.confirm(`Delete order #${order.order_number || order.id?.slice(-8)}?\n\nThis action cannot be undone.`)) {
                                    deleteOrder(order.id);
                                  }
                                }}
                                className="text-red-500 hover:text-red-700 text-sm font-medium"
                                title="Delete Order"
                              >
                                🗑️
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'stats' && (
              <div className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Statistics</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="text-4xl font-bold text-gray-900">{totalOrders}</div>
                    <div className="text-gray-500">Total Orders</div>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="text-4xl font-bold text-green-600">${totalRevenue.toFixed(2)}</div>
                    <div className="text-gray-500">Total Revenue</div>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="text-4xl font-bold text-blue-600">{completedOrders}</div>
                    <div className="text-gray-500">Completed</div>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="text-4xl font-bold text-yellow-600">{pendingOrders}</div>
                    <div className="text-gray-500">Pending</div>
                  </div>
                </div>
              </div>
            )}

            {/* Latest Files Tab */}
            {activeTab === 'latest-files' && (
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Latest Files</h2>
                  <button
                    onClick={() => { resetLatestFileForm(); setShowLatestFileForm(true); }}
                    className="bg-gradient-to-r from-blue-500 to-cyan-400 text-white px-6 py-2 rounded-xl font-semibold hover:shadow-lg transition"
                  >
                    + Add New File
                  </button>
                </div>

                {/* Add/Edit Form */}
                {showLatestFileForm && (
                  <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-bold text-gray-900">
                        {editingLatestFile ? 'Edit File' : 'Add New ECU File'}
                      </h3>
                      <button onClick={resetLatestFileForm} className="text-gray-400 hover:text-gray-600">✕</button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Main ECU File Data Section */}
                      <div className="md:col-span-2">
                        <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                          <span className="bg-blue-100 p-2 rounded-lg">📋</span>
                          Main ECU File Data
                        </h4>
                      </div>

                      {/* Required Fields */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Calibration No / Software No *</label>
                        <input
                          type="text"
                          name="calibration_no"
                          value={latestFileForm.calibration_no}
                          onChange={handleLatestFileFormChange}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., 0281020075"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ECU Type / Block Type *</label>
                        <input
                          type="text"
                          name="ecu_type"
                          value={latestFileForm.ecu_type}
                          onChange={handleLatestFileFormChange}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., Bosch EDC17C46"
                          required
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Details *</label>
                        <input
                          type="text"
                          name="vehicle_details"
                          value={latestFileForm.vehicle_details}
                          onChange={handleLatestFileFormChange}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., BMW 320d F30 2015 2.0L Diesel 184HP"
                          required
                        />
                      </div>

                      {/* Optional Fields */}
                      <div className="md:col-span-2 border-t pt-4 mt-2">
                        <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                          <span className="bg-gray-100 p-2 rounded-lg">⚙️</span>
                          Optional Details
                        </h4>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Brand / Manufacturer</label>
                        <input
                          type="text"
                          name="brand"
                          value={latestFileForm.brand}
                          onChange={handleLatestFileFormChange}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., BMW"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Year Range</label>
                        <input
                          type="text"
                          name="year_range"
                          value={latestFileForm.year_range}
                          onChange={handleLatestFileFormChange}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., 2015-2020"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Engine Type</label>
                        <input
                          type="text"
                          name="engine_type"
                          value={latestFileForm.engine_type}
                          onChange={handleLatestFileFormChange}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., 2.0L TDI 184HP"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Thumbnail URL</label>
                        <input
                          type="text"
                          name="thumbnail_url"
                          value={latestFileForm.thumbnail_url}
                          onChange={handleLatestFileFormChange}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="https://..."
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          name="description"
                          value={latestFileForm.description}
                          onChange={handleLatestFileFormChange}
                          rows={3}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Additional details about this file..."
                        />
                      </div>

                      {/* Original Files Section */}
                      <div className="md:col-span-2 border-t pt-4 mt-2">
                        <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                          <span className="bg-green-100 p-2 rounded-lg">📥</span>
                          Original File(s)
                        </h4>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Upload Original ECU File *</label>
                        <input
                          type="file"
                          onChange={(e) => handleLatestFileUpload(e, 'original_file')}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                          disabled={uploadingLatestFile}
                        />
                        {uploadingLatestFile && uploadingFileType === 'original_file' && (
                          <div className="text-sm text-blue-600 mt-1">Uploading...</div>
                        )}
                        {latestFileForm.original_file_name && (
                          <div className="text-sm text-green-600 mt-1">✅ {latestFileForm.original_file_name}</div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Original File Price ($) *</label>
                        <input
                          type="number"
                          step="0.01"
                          name="original_price"
                          value={latestFileForm.original_price}
                          onChange={handleLatestFileFormChange}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., 25.00"
                          required
                        />
                      </div>

                      {/* Modified Files Section */}
                      <div className="md:col-span-2 border-t pt-4 mt-2">
                        <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                          <span className="bg-orange-100 p-2 rounded-lg">📤</span>
                          Modified File(s)
                        </h4>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Type of Modification *</label>
                        <input
                          type="text"
                          name="modification_type"
                          value={latestFileForm.modification_type}
                          onChange={handleLatestFileFormChange}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., Stage 1, DPF Off, EGR Off"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Modified File Price ($) *</label>
                        <input
                          type="number"
                          step="0.01"
                          name="modified_price"
                          value={latestFileForm.modified_price}
                          onChange={handleLatestFileFormChange}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., 75.00"
                          required
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Upload Modified ECU File *</label>
                        <input
                          type="file"
                          onChange={(e) => handleLatestFileUpload(e, 'modified_file')}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                          disabled={uploadingLatestFile}
                        />
                        {uploadingLatestFile && uploadingFileType === 'modified_file' && (
                          <div className="text-sm text-blue-600 mt-1">Uploading...</div>
                        )}
                        {latestFileForm.modified_file_name && (
                          <div className="text-sm text-green-600 mt-1">✅ {latestFileForm.modified_file_name}</div>
                        )}
                      </div>

                      {/* Featured File Section */}
                      <div className="md:col-span-2 border-t pt-4 mt-2">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            name="is_featured"
                            checked={latestFileForm.is_featured}
                            onChange={handleLatestFileFormChange}
                            className="w-5 h-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
                          />
                          <span className="font-semibold text-gray-800 flex items-center gap-2">
                            <span className="bg-yellow-100 p-2 rounded-lg">⭐</span>
                            Mark as Featured File
                          </span>
                        </label>
                        <p className="text-sm text-gray-500 mt-1 ml-8">
                          Featured files appear at the top with a golden badge - great for promotions
                        </p>
                      </div>

                      {/* DCU/SCR Files Section */}
                      <div className="md:col-span-2 border-t pt-4 mt-2">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            name="has_dcu"
                            checked={latestFileForm.has_dcu}
                            onChange={handleLatestFileFormChange}
                            className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="font-semibold text-gray-800 flex items-center gap-2">
                            <span className="bg-purple-100 p-2 rounded-lg">🔗</span>
                            Include Dosing Control Unit (SCR/DCU) Files
                          </span>
                        </label>
                        <p className="text-sm text-gray-500 mt-1 ml-8">
                          Check this if the file requires linked DCU files (sold as bundle)
                        </p>
                      </div>

                      {latestFileForm.has_dcu && (
                        <>
                          {/* DCU Details */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">DCU Calibration No / Software No *</label>
                            <input
                              type="text"
                              name="dcu_calibration_no"
                              value={latestFileForm.dcu_calibration_no || ''}
                              onChange={handleLatestFileFormChange}
                              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="e.g., 0281020075"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">DCU Type / Block Type *</label>
                            <input
                              type="text"
                              name="dcu_ecu_type"
                              value={latestFileForm.dcu_ecu_type || ''}
                              onChange={handleLatestFileFormChange}
                              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="e.g., Bosch Denoxtronic 2.2"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Upload Original DCU File</label>
                            <input
                              type="file"
                              onChange={(e) => handleLatestFileUpload(e, 'original_dcu')}
                              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                              disabled={uploadingLatestFile}
                            />
                            {uploadingLatestFile && uploadingFileType === 'original_dcu' && (
                              <div className="text-sm text-blue-600 mt-1">Uploading...</div>
                            )}
                            {latestFileForm.original_dcu_name && (
                              <div className="text-sm text-green-600 mt-1">✅ {latestFileForm.original_dcu_name}</div>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Upload Modified DCU File</label>
                            <input
                              type="file"
                              onChange={(e) => handleLatestFileUpload(e, 'modified_dcu')}
                              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                              disabled={uploadingLatestFile}
                            />
                            {uploadingLatestFile && uploadingFileType === 'modified_dcu' && (
                              <div className="text-sm text-blue-600 mt-1">Uploading...</div>
                            )}
                            {latestFileForm.modified_dcu_name && (
                              <div className="text-sm text-green-600 mt-1">✅ {latestFileForm.modified_dcu_name}</div>
                            )}
                          </div>
                        </>
                      )}

                      {/* Action Buttons */}
                      <div className="md:col-span-2 flex gap-4 pt-4 border-t mt-4">
                        <button
                          onClick={handleSaveLatestFile}
                          disabled={uploadingLatestFile}
                          className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-400 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition disabled:opacity-50"
                        >
                          {editingLatestFile ? 'Update File' : 'Save File'}
                        </button>
                        <button
                          onClick={resetLatestFileForm}
                          className="px-8 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Files List */}
                {latestFilesLoading ? (
                  <div className="text-center py-12 text-gray-500">Loading files...</div>
                ) : latestFiles.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                    <div className="text-6xl mb-4">📁</div>
                    <div className="text-gray-500 mb-4">No files added yet</div>
                    <button
                      onClick={() => { resetLatestFileForm(); setShowLatestFileForm(true); }}
                      className="text-blue-600 hover:underline"
                    >
                      Add your first file →
                    </button>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">File Details</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Vehicle</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Prices</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                          <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {latestFiles.map((file) => (
                          <tr key={file.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900">{file.calibration_no}</span>
                                {file.is_featured && <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded font-semibold">⭐ Featured</span>}
                              </div>
                              <div className="text-sm text-gray-500">{file.ecu_type}</div>
                              <div className="text-xs text-blue-600">{file.modification_type}</div>
                              {file.has_dcu && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">+DCU</span>}
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">{file.vehicle_details}</div>
                              {file.brand && <div className="text-xs text-gray-500">{file.brand} {file.year_range}</div>}
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm">
                                <span className="text-green-600">Original: ${file.original_price}</span>
                              </div>
                              <div className="text-sm">
                                <span className="text-orange-600">Modified: ${file.modified_price}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <button
                                onClick={() => handleToggleLatestFileActive(file)}
                                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                  file.is_active 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-gray-100 text-gray-500'
                                }`}
                              >
                                {file.is_active ? 'Active' : 'Inactive'}
                              </button>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button
                                onClick={() => handleEditLatestFile(file)}
                                className="text-blue-600 hover:text-blue-800 mr-3"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteLatestFile(file.id)}
                                className="text-red-600 hover:text-red-800"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>
      
      {/* Order Detail Sidebar */}
      {selectedOrder && (
        <aside className="w-96 bg-white border-l border-gray-200 overflow-y-auto">
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900">Order Details</h3>
              <button
                onClick={() => setSelectedOrder(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            {/* Order ID */}
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <div className="text-xs text-gray-500 mb-1">Order ID</div>
              <div className="font-mono text-sm">{selectedOrder.id}</div>
            </div>
            
            {/* Customer Info */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-900 mb-3">Customer</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">Name</span>
                  <span className="font-medium">{selectedOrder.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Email</span>
                  <span className="font-medium text-blue-600">{selectedOrder.customer_email}</span>
                </div>
              </div>
            </div>
            
            {/* Vehicle Details - Enhanced */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-900 mb-3">🚗 Vehicle Details</h4>
              {(selectedOrder.vehicle_info || selectedOrder.vehicle_make || selectedOrder.vehicle || selectedOrder.selected_vehicle) ? (
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-4">
                  {/* Main Vehicle Info */}
                  <div className="text-lg font-semibold text-gray-900 mb-2">
                    {selectedOrder.vehicle_info || 
                     `${selectedOrder.vehicle_year || ''} ${selectedOrder.vehicle_make || ''} ${selectedOrder.vehicle_model || ''}`.trim() ||
                     (selectedOrder.vehicle && `${selectedOrder.vehicle.year || ''} ${selectedOrder.vehicle.make || ''} ${selectedOrder.vehicle.model || ''}`.trim()) ||
                     (selectedOrder.selected_vehicle && `${selectedOrder.selected_vehicle.year || ''} ${selectedOrder.selected_vehicle.make || ''} ${selectedOrder.selected_vehicle.model || ''}`.trim()) ||
                     'Vehicle info not specified'}
                  </div>
                  
                  {/* Detailed breakdown - supports both flat fields (vehicle_make) and nested objects (vehicle.make) */}
                  <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                    {(selectedOrder.vehicle_make || selectedOrder.vehicle?.make || selectedOrder.selected_vehicle?.make) && (
                      <div>
                        <span className="text-gray-500">Make:</span>
                        <span className="ml-2 font-medium">{selectedOrder.vehicle_make || selectedOrder.vehicle?.make || selectedOrder.selected_vehicle?.make}</span>
                      </div>
                    )}
                    {(selectedOrder.vehicle_model || selectedOrder.vehicle?.model || selectedOrder.selected_vehicle?.model) && (
                      <div>
                        <span className="text-gray-500">Model:</span>
                        <span className="ml-2 font-medium">{selectedOrder.vehicle_model || selectedOrder.vehicle?.model || selectedOrder.selected_vehicle?.model}</span>
                      </div>
                    )}
                    {(selectedOrder.vehicle_year || selectedOrder.vehicle?.year || selectedOrder.selected_vehicle?.year) && (
                      <div>
                        <span className="text-gray-500">Year:</span>
                        <span className="ml-2 font-medium">{selectedOrder.vehicle_year || selectedOrder.vehicle?.year || selectedOrder.selected_vehicle?.year}</span>
                      </div>
                    )}
                    {(selectedOrder.vehicle_engine || selectedOrder.vehicle?.engine || selectedOrder.selected_vehicle?.engine || selectedOrder.engine_info) && (
                      <div>
                        <span className="text-gray-500">Engine:</span>
                        <span className="ml-2 font-medium">{selectedOrder.vehicle_engine || selectedOrder.vehicle?.engine || selectedOrder.selected_vehicle?.engine || selectedOrder.engine_info}</span>
                      </div>
                    )}
                    {(selectedOrder.vehicle_ecu || selectedOrder.vehicle?.ecu || selectedOrder.selected_vehicle?.ecu || selectedOrder.ecu_type) && (
                      <div className="col-span-2">
                        <span className="text-gray-500">ECU:</span>
                        <span className="ml-2 font-medium text-blue-600">{selectedOrder.vehicle_ecu || selectedOrder.vehicle?.ecu || selectedOrder.selected_vehicle?.ecu || selectedOrder.ecu_type}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Vehicle Type Badge */}
                  {(selectedOrder.vehicle_type || selectedOrder.selected_vehicle_type) && (
                    <div className="mt-3">
                      <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
                        {selectedOrder.vehicle_type || selectedOrder.selected_vehicle_type}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center text-gray-400">
                  No vehicle information provided
                </div>
              )}
            </div>
            
            {/* Services */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-900 mb-3">Services</h4>
              <div className="space-y-2">
                {(selectedOrder.services || selectedOrder.selected_services || []).map((s, i) => (
                  <div key={i} className="bg-blue-50 text-blue-700 px-3 py-2 rounded-lg text-sm">
                    {typeof s === 'object' ? s.name : s}
                  </div>
                ))}
              </div>
              <div className="mt-3 text-right">
                <span className="text-gray-500">Total:</span>
                <span className="text-xl font-bold text-gray-900 ml-2">
                  ${(selectedOrder.total_amount || selectedOrder.price || 0).toFixed(2)}
                </span>
              </div>
            </div>
            
            {/* Status Update */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-900 mb-3">Update Status</h4>
              <div className="grid grid-cols-2 gap-2">
                {['pending', 'processing', 'completed', 'cancelled'].map(status => (
                  <button
                    key={status}
                    onClick={() => updateStatus(selectedOrder.id, status)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                      selectedOrder.status === status
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Files Section */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-900 mb-3">📁 Files</h4>
              
              {/* Original File - Customer's Upload */}
              {(selectedOrder.original_file || selectedOrder.uploaded_files?.length > 0) ? (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-blue-600 font-medium mb-1">📥 CUSTOMER'S ORIGINAL FILE</div>
                      <div className="text-sm font-medium text-gray-900">
                        {selectedOrder.original_filename || selectedOrder.uploaded_files?.[0]?.original_name || 'ECU File'}
                      </div>
                      {selectedOrder.file_size_mb && (
                        <div className="text-xs text-gray-500">{selectedOrder.file_size_mb?.toFixed(2)} MB</div>
                      )}
                    </div>
                    <button
                      onClick={() => downloadFile(selectedOrder.original_file || selectedOrder.uploaded_files?.[0]?.file_id)}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
                    >
                      ⬇️ Download
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-3 text-center">
                  <span className="text-gray-400">No original file found</span>
                </div>
              )}
              
              {/* Modified File - Your Upload */}
              {selectedOrder.modified_file ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-green-600 font-medium mb-1">✅ MODIFIED FILE (Ready)</div>
                      <div className="text-sm font-medium text-green-700">
                        {selectedOrder.modified_filename || 'Modified ECU File'}
                      </div>
                    </div>
                    <button
                      onClick={() => downloadFile(selectedOrder.modified_file)}
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
                    >
                      ⬇️ Download
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-3">
                  <div className="text-xs text-yellow-700 font-medium mb-2">⏳ UPLOAD MODIFIED FILE</div>
                  <p className="text-sm text-yellow-600 mb-3">
                    Download the customer's file above, process it, then upload the modified version here.
                  </p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => uploadModifiedFile(e.target.files?.[0])}
                    className="hidden"
                    accept=".bin,.hex,.ecu,.ori,.mod,.fls,.mpc"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFile}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-lg text-sm font-medium transition disabled:opacity-50"
                  >
                    {uploadingFile ? `Uploading... ${uploadProgress}%` : '📤 Upload Modified File'}
                  </button>
                </div>
              )}
            </div>
            
            {/* Send to Customer Section */}
            {selectedOrder.modified_file && (
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">📧 Send to Customer</h4>
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-sm text-gray-600 mb-3">
                    Send download link to <strong>{selectedOrder.customer_email}</strong>
                  </p>
                  <button
                    onClick={sendDownloadEmail}
                    disabled={sendingEmail}
                    className={`w-full py-3 rounded-xl font-medium transition ${
                      emailSent
                        ? 'bg-green-500 text-white'
                        : 'bg-gradient-to-r from-blue-500 to-cyan-400 text-white hover:shadow-lg'
                    } disabled:opacity-50`}
                  >
                    {emailSent ? '✓ Email Sent!' : sendingEmail ? 'Sending...' : '📧 Send Download Link Email'}
                  </button>
                </div>
              </div>
            )}
            
            {/* Message Customer */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-900 mb-3">Message Customer</h4>
              <textarea
                value={adminMessage}
                onChange={(e) => setAdminMessage(e.target.value)}
                placeholder="Type a message to the customer..."
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                rows={3}
              />
              <button
                onClick={sendAdminMessage}
                disabled={sendingMessage || !adminMessage.trim()}
                className="w-full mt-2 bg-gray-900 text-white py-2 rounded-xl text-sm font-medium hover:bg-gray-800 transition disabled:opacity-50"
              >
                {sendingMessage ? 'Sending...' : 'Send Message'}
              </button>
            </div>
            
            {/* Quick Actions */}
            <div className="pt-4 border-t border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-3">Quick Actions</h4>
              <div className="space-y-2">
                <button
                  onClick={() => {
                    updateStatus(selectedOrder.id, 'completed');
                    if (selectedOrder.modified_file) {
                      sendDownloadEmail();
                    }
                  }}
                  className="w-full bg-green-500 hover:bg-green-600 text-white py-2 rounded-xl text-sm font-medium transition"
                >
                  ✓ Mark Complete & Notify
                </button>
              </div>
            </div>
          </div>
        </aside>
      )}
    </div>
  );
};

export default AdminPage;
